import {
  DEFAULT_WQ_TYPE,
  WQ_STORAGE_ERROR_MESSAGE,
} from '../constants/Queue.constants';

/**
 * getAllWqPersistStates
 *
 * @param {string} wqType - work queue name
 * ex, 'workQueue' - for default one, '3' - Test Organization A (office 3)
 *
 * @returns {{sorting: [{id: string, desc?: boolean, value?: string}], filtering:  [{id: string, desc?: boolean, value?: string, filter?: string}] }} - returns all persisted state
 * ex, {{sorting: [{id: facility, desc?: true, value?: string}], filtering:  [{id: facility, desc?: boolean, value?: AT, filter?: string}] }}
 */
export const getAllWqPersistStates = (wqType) => {
  const savedState = localStorage.getItem(wqType);

  try {
    return savedState ? JSON.parse(savedState) : {};
  } catch (error) {
    console.error(`${WQ_STORAGE_ERROR_MESSAGE} (${wqType}):`, error);
    return {};
  }
};

/**
 * getWqPersistState
 *
 * @param {string} wqType - work queue name
 * ex, 'workQueue' - for default one, '3' - Test Organization A (office 3).
 *
 * @returns  {{sorting: [{id: string, desc?: boolean, value?: string}], filtering:  [{id: string, desc?: boolean, value?: string, filter?: string}] }}- returns persisted sorting state based on wqType
 * ex, {{sorting: [{id: facility, desc?: true, value?: string}], filtering:  [{id: facility, desc?: boolean, value?: AT, filter?: string}] }}
 */
export const getWqPersistState = (wqType = DEFAULT_WQ_TYPE) => {
  const allStates = getAllWqPersistStates(wqType);

  return {
    sorting: allStates[wqType]?.sorting || [],
    filtering: allStates[wqType]?.filtering || [],
  };
};

/**
 * saveWqPersistState
 *
 * @param {string} state - updated state (after handling sorting/filter)
 * ex, [{"id": "facility", "desc": true}]
 *
 * @param {string} wqType - work queue name
 * ex, 'workQueue' - for default one, '3' - Test Organization A (office 3).
 *
 */
export const saveWqPersistState = ({
  sortingState,
  filteringState,
  wqType = DEFAULT_WQ_TYPE,
}) => {
  const allStates = getAllWqPersistStates(wqType);
  const currentWqState = allStates[wqType] || {};

  currentWqState.sorting = sortingState;
  currentWqState.filtering = filteringState;

  allStates[wqType] = currentWqState;

  localStorage.setItem(wqType, JSON.stringify(allStates));
};

/**
 * saveWqPersistState
 *
 * @param {string} wqType - work queue name
 * ex, 'workQueue' - for default one, '3' - Test Organization A (office 3).
 */
export const removeWqPersistState = (wqType) => {
  const allStates = getAllWqPersistStates(`${wqType}ColumnState`);

  delete allStates[`${wqType}ColumnState`];

  localStorage.setItem(`${wqType}ColumnState`, JSON.stringify(allStates));
};

/**
 * clearAllWqPersistStates
 *
 * @todo add removing other storage types in the future
 * ex, 'wqFilterState', 'wqPopulationSortingState', etc
 */
export const clearAllWqPersistStates = () => {
  const keysToRemove = Object.keys(localStorage).filter((key) =>
    key.includes('ColumnState')
  );
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};
